import { NumberButtonType } from '@components/PropertiesSearch/PropertySearchFilters';
import { BedsBathsSection } from '@components/FilterComponents/BedsBathsSection';
import { ButtonSection } from '@components/FilterComponents/ButtonSection';
import { CheckboxSection } from '@components/FilterComponents/CheckboxSection';
import { ForSaleSection } from '@components/FilterComponents/ForSaleSection';
import { PriceSection } from '@components/FilterComponents/PriceSection';
import brand from '@design/brand';
import {
  ParkingFeatureEnum,
  PropertySubTypeEnum,
  PropertyTypeEnum,
} from '@engelvoelkersamericas/api';
import { Close, Filter } from '@icon/index';
import { Dayjs } from 'dayjs';
import { useTranslations } from 'next-intl';
import { ChangeEvent, FC } from 'react';
import { FieldType, Lifestyle } from '../../PropertySearchFilters';
import {
  FButton,
  FButtonWrapper,
  FCloseButton,
  FModalWrapper,
  ModalHeader,
  ModalSection,
} from '../../PropertySearchFilters/styles';

export interface FilterButtonProps {
  selected: boolean;
  name: string;
  title: string;
  handleSetSelectedFilter: (f: string) => void; // Function for setting active filter
  propertyTypes: { title: string; value: PropertySubTypeEnum[] }[]; // FOR SALE - For rending available property types
  selectedPropertyTypes: PropertySubTypeEnum[];
  handleSetArr: (type: string, b: string | string[]) => void;
  handleNumberButtonChange: (type: NumberButtonType, value: number | null) => void;
  listingTypes: { title: string; value: PropertyTypeEnum[] }[];
  selectedListingTypes: PropertyTypeEnum[];
  priceInterval?: number; // PRICE - interval at which you can select prices
  minPrice?: number;
  maxPrice?: number;
  maxAllowedPrice?: number;
  handleSliderChange: (event: Event, newValue: number | number[]) => void; // PRICE - used for slider
  handleTextFieldChange: (e: ChangeEvent<HTMLInputElement>, field: FieldType, min?: 'min') => void; // PRICE - used for text fields
  bedrooms: { title: string; value: number | null }[]; // BEDS & BATHS - Rendering available bedroom types
  bathrooms: { title: string; value: number }[]; // BEDS & BATHROOMS - Rendering available bathroom types
  selectedBedrooms?: number;
  selectedBathrooms?: number;
  sqft?: number[];
  minLotSize?: number;
  maxLotSize?: number;
  maxAllowedSqft?: number;
  maxAllowedLotSize?: number;
  selectedListingStatus?: string[];
  garage?: {
    vehicles: { title: string; value: number }[];
    type: { title: string; value: ParkingFeatureEnum }[];
  };
  selectedGarage?: {
    vehicles?: number;
    type?: ParkingFeatureEnum[];
  };
  views?: string[];
  selectedViews?: string[];
  amenities: string[];
  selectedAmenities: string[];
  years?: number[];
  handleDateChange?: (d: Dayjs | null, first?: true) => void;
  stories?: number[];
  maxStories?: number;
  listingStatus?: string[];
  onClickApply: () => void;
  onClickClear: (type: string) => void;
  onClickClose?: (type: string) => void;
  lifestyle?: Lifestyle;
  isFilterApplied?: boolean;
  hideRentFilters?: boolean;
}

// PARENT COMPONENT
const FilterButton: FC<FilterButtonProps> = ({
  selected,
  name,
  handleSetSelectedFilter,
  propertyTypes,
  listingTypes,
  selectedPropertyTypes,
  selectedListingTypes,
  minPrice,
  maxPrice,
  maxAllowedPrice,
  priceInterval,
  handleSliderChange,
  handleTextFieldChange,
  bedrooms,
  bathrooms,
  handleSetArr,
  handleNumberButtonChange,
  selectedBedrooms,
  selectedBathrooms,
  onClickApply,
  onClickClear,
  onClickClose,
  title,
  amenities,
  selectedAmenities,
  isFilterApplied,
  hideRentFilters = false,
}) => {
  const t = useTranslations();
  const allFilters = name === 'allFilters';
  return (
    <FButtonWrapper
      $selected={selected}
      $allFilters={allFilters}
      $isFilterApplied={isFilterApplied}
    >
      <FButton
        $selected={selected}
        onClick={() => handleSetSelectedFilter(name)}
        $all={allFilters}
        $isFilterApplied={isFilterApplied}
        data-testid={`${name.toLowerCase()}-filter-button`}
      >
        {title}
        {name === 'allFilters' && <Filter />}
      </FButton>
      {isFilterApplied && name !== 'allFilters' && onClickClose && (
        <FCloseButton onClick={() => onClickClose(name)}>
          <Close color={brand.colors.black} />
        </FCloseButton>
      )}
      {selected && (
        <FModalWrapper $open={selected && allFilters}>
          {(name === 'forSale' || name === 'listingType') && (
            <ForSaleSection
              propertyTypes={propertyTypes}
              listingTypes={listingTypes}
              handleSetCheckbox={handleSetArr}
              selectedListingTypes={selectedListingTypes}
              selectedPropertyTypes={selectedPropertyTypes}
              hideRentFilters={hideRentFilters}
            />
          )}
          {name === 'price' && (
            <PriceSection
              minPrice={minPrice}
              maxPrice={maxPrice}
              handleSliderChange={handleSliderChange}
              maxAllowedPrice={maxAllowedPrice}
              priceInterval={priceInterval}
              handleTextFieldChange={handleTextFieldChange}
            />
          )}
          {name === 'bedsAndBath' && (
            <BedsBathsSection
              bedrooms={bedrooms}
              bathrooms={bathrooms}
              bedsValue={selectedBedrooms}
              bathsValue={selectedBathrooms}
              onBedsChange={(val) => handleNumberButtonChange('bedrooms', val)}
              onBathroomsChange={(val) => handleNumberButtonChange('bathrooms', val)}
            />
          )}
          {name === 'amenities' && (
            <ModalSection>
              <ModalHeader>{t('Amenities')}</ModalHeader>
              <CheckboxSection
                items={amenities}
                handleSetSelected={handleSetArr}
                selectedArr={selectedAmenities}
                type="amenities"
              />
            </ModalSection>
          )}
          {!allFilters && (
            <ButtonSection
              onClickApply={onClickApply}
              onClickClear={onClickClear}
              name={name}
              isDisable={name === 'price' && minPrice && maxPrice ? minPrice > maxPrice : false}
            />
          )}
        </FModalWrapper>
      )}
    </FButtonWrapper>
  );
};

export default FilterButton;
